<script setup lang="ts">
import { useNews } from '../../../composables/useNews';
import { useElementSize } from '@vueuse/core'

const { importantNews } = useNews();

const ANIMATION_BASE_TIME = 15;


const animationTime = ref(ANIMATION_BASE_TIME);
const isReady = ref(false);
const isAnimationEnabled = ref(false);

const contentWrapper = ref(null);
const { width: contentWrapperWidth } = useElementSize(contentWrapper);

const originalNewsContainer = ref(null);
const { width: newsWidth } = useElementSize(originalNewsContainer, undefined, { box: 'border-box' });


watchEffect(() => {
    if (importantNews.value?.length && newsWidth.value && contentWrapperWidth.value) {
        isReady.value = true
        animationTime.value = Math.ceil(Math.ceil((newsWidth.value / window.innerWidth)) * ANIMATION_BASE_TIME);
        isAnimationEnabled.value = newsWidth.value > contentWrapperWidth.value;
    }
})

// function htmlToPlainText(html) {
//   const temp = document.createElement('div');
//   temp.innerHTML = html;

//   // `innerText` preserves spacing similar to getText()
//   return temp.innerText.trim();
// }

function sanitizeInlineHtml(html:string) {
  if (!html) return '';

  return html
    // Replace <br> and paragraph boundaries with space
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>|<\/div>/gi, ' ')
    .replace(/<p[^>]*>|<div[^>]*>/gi, '')

    // Strip all tags except inline ones
    .replace(/<(?!\/?(b|strong|i|em|u|span)(\s|>|$))[^>]+>/gi, '')

    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
}


</script>

<template>
    <div v-if="importantNews?.length"
        class="importantNews m-0 text-lg text-black transition-all duration-500 ease-in-out shadow-sm"
        :class="{ isReady, isAnimationEnabled }">
        <div class="overflow-hidden">
            <div ref="contentWrapper" class="contentWrapper"
                :style="`--animation-offset-width: -${newsWidth}px; --animation-time:${animationTime}s;`">

                <div ref="originalNewsContainer" class="flex gap-8 pr-8 shrink-0 min-w-full items-center">
                    <template v-for="(notif, idx) in importantNews" :key="`notification-orig-${notif.id}`">
                        <div class="min-w-[max-content] flex gap-1">
                            <b v-if="notif.title" class="mr-1">{{ notif.title }}</b>
                            <p v-if="notif.description" v-html="sanitizeInlineHtml(notif.description)"></p>
                        </div>
                        <i class="w-2 aspect-square bg-black rounded-full" :data-test="idx"
                            :class="{ 'opacity-0': !isAnimationEnabled && idx === importantNews.length - 1 }"></i>
                    </template>
                </div>

                <div class="flex gap-8 shrink-0 items-center">
                    <template v-for="(notif) in importantNews" :key="`notification-copy-${notif.id}`">
                        <div class="min-w-[max-content] flex gap-1">
                            <b v-if="notif.title" class="mr-1">{{ notif.title }}</b>
                            <p v-if="notif.description" v-html="sanitizeInlineHtml(notif.description)"></p>
                        </div>
                        <i class="w-2 aspect-square bg-black rounded-full"></i>
                    </template>
                </div>
            </div>
        </div>
    </div>

</template>


<style scoped>
.importantNews {
    background: linear-gradient(90deg, var(--pd-clr-danger-400), var(--pd-clr-warning-400));
    background-size: 400% 400%;

    -webkit-animation: AnimationName 6s ease infinite;
    -moz-animation: AnimationName 6s ease infinite;
    animation: AnimationName 6s ease infinite;
}

@-webkit-keyframes AnimationName {
    0% {
        background-position: 0% 50%
    }

    50% {
        background-position: 100% 50%
    }

    100% {
        background-position: 0% 50%
    }
}

@-moz-keyframes AnimationName {
    0% {
        background-position: 0% 50%
    }

    50% {
        background-position: 100% 50%
    }

    100% {
        background-position: 0% 50%
    }
}

@keyframes AnimationName {
    0% {
        background-position: 0% 50%
    }

    50% {
        background-position: 100% 50%
    }

    100% {
        background-position: 0% 50%
    }
}



@keyframes slideLeft {
    0% {
        transform: translateX(0);
    }

    100% {
        transform: translateX(var(--animation-offset-width));
    }
}

.importantNews {
    overflow: hidden;
    padding: 0 1rem;
    height: 0px;
}

.importantNews.isReady {
    padding: 0.5rem 1rem;
    height: 41px;
}


.importantNews .contentWrapper {
    display: flex;
}

.importantNews.isReady.isAnimationEnabled .contentWrapper {
    animation-name: slideLeft;
    animation-duration: var(--animation-time);
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-delay: 2000ms;
}
</style>